import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AccountService } from './account.service';
import { AuthServerProvider } from './auth-jwt.service';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
@Injectable()
export class Principal {

    private userIdentity: any;
    private authenticated = false;
    private authenticationState = new Subject<any>();

    constructor(
        private account: AccountService,
        private auth: AuthServerProvider,
        private toastr: ToastrService,
        private router: Router
    ) { 
        window.addEventListener('storage', (event) => {
            if (event.key === 'access_token') {
                if (!event.newValue) {
                    this.authenticate(null);
                    this.router.navigate(['/login']);
                } else if (event.newValue !== event.oldValue) {
                    this.userIdentity = undefined;
                    this.authenticated = false;
                    this.authenticationState.next(null);
                    this.router.navigate(['/login']);
                }
            }
        });
    }

    authenticate(identity: any) {
        this.userIdentity = identity;
        this.authenticated = identity !== null;
        this.authenticationState.next(this.userIdentity);
    }

    hasAnyAuthority(authorities: string[]): Promise<boolean> {
        return Promise.resolve(true);
    }

    hasAuthority(authority: string): Promise<boolean> {
        if (!this.authenticated) {
            return Promise.resolve(false);
        }
        return this.identity().then((id) => {
            return id?.authorities?.includes(authority) ?? false;
        });
    }

    identity(force?: boolean): Promise<any> {
        if (force === true) this.userIdentity = undefined;
        if (this.userIdentity) {
            const isAccessExpired: boolean = this.isAccessExpired(this.userIdentity.access_expiry_date);
            if (isAccessExpired) {
                return Promise.resolve(this.handleAccessExpired());
            }
            return Promise.resolve(this.userIdentity);
        }

        return this.account.get().toPromise().then((response: any) => {
            const account = response.body;

            if (account && this.isAccessExpired(account.access_expiry_date)) {
                return this.handleAccessExpired();
            }

            if (account) {
                const allRoles: string[] = account.realm_access?.roles || [];
                
                const appRoles = ['ADMIN', 'DEVELOPER', 'VIEWER'];
                const roles = allRoles.filter((r: string) => appRoles.includes(r));
                const permissions = allRoles.filter((r: string) => !appRoles.includes(r) 
                    && !r.startsWith('default-roles') 
                    && r !== 'offline_access' 
                    && r !== 'uma_authorization');

                this.auth.storeRoles(roles);
                if (roles.includes('ADMIN')) {
                    localStorage.removeItem('permissions');
                } else {
                    this.auth.storePermissions(permissions);
                }
                account.authorities = roles;
                account.permissions = permissions;

                this.auth.storeAuthenticationUsername(account.preferred_username);
                this.auth.storeUserId(account._id);
                this.userIdentity = account;
                this.authenticated = true;
            } else {
                this.userIdentity = null;
                this.authenticated = false;
            }
            this.authenticationState.next(this.userIdentity);
            return this.userIdentity;
        }).catch((err) => {
            if (err.status && err.status !== 401) {
                if (err.error?.message !== 'No Authorization was found in request.headers') {
                    this.toastr.error(err.error?.message, 'Error');
                }
            }
            this.userIdentity = null;
            this.authenticated = false;
            this.authenticationState.next(this.userIdentity);
            return null;
        });
    }

    isAuthenticated(): boolean {
        return this.authenticated;
    }

    isIdentityResolved(): boolean {
        return this.userIdentity !== undefined;
    }

    getAuthenticationState(): Observable<any> {
        return this.authenticationState.asObservable();
    }

    private isAccessExpired(expiry?: string | null): boolean {
        if (!expiry) return false;
        const expiryTime = new Date(expiry).getTime();
        return !isNaN(expiryTime) && Date.now() > expiryTime;   // same strict > as generateToken
    }

    private handleAccessExpired(): null {
        this.userIdentity = null;
        this.authenticated = false;
        this.authenticationState.next(null);

        this.auth.clearLocalStorage();
        this.toastr.error('Your access has expired. Please contact an administrator.', 'Access Expired');
        this.router.navigate(['/login']);
        return null;
    }
}